import { inject, Injectable } from '@angular/core';
import { SubmissionsDomainModel } from '../../../../../../domainModels/submissions-domain-model';
import { ToastService } from '../../../../../../services/toast-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, finalize, switchMap, take } from 'rxjs/operators';
import { MemberSubmission } from '../../../../../../models/submissions/member-submission';
import { SubmissionGroup } from '../../../../../../models/users/enum/submission-type-enum';
import { Subscribable } from '../../../../../../models/base/subscribable';
import { ODataQueryOptions } from '../../../../../../models/shared/odata-query-options';

@Injectable()
export class MemberSubmissionHistoryService extends Subscribable {
  constructor() {
    super();
  }

  private submissionDomainModel = inject(SubmissionsDomainModel);
  private toastService = inject(ToastService);

  private _isLoading = new BehaviorSubject<boolean>(true);
  public isLoading$ = this._isLoading as Observable<boolean>;

  private _submissionGroupFilter = new BehaviorSubject<SubmissionGroup | null>(null);
  public submissionGroupFilter$ = this._submissionGroupFilter as Observable<SubmissionGroup | null>;
  public setSubmissionGroupFilter = (submissionGroup: SubmissionGroup) =>
    this._submissionGroupFilter.next(submissionGroup);

  private _memberSubmissions = new BehaviorSubject<MemberSubmission[] | null>(null);
  public memberSubmissions$ = this._memberSubmissions as Observable<MemberSubmission[] | null>;

  private listenToSubmissionGroupFilter = this.submissionGroupFilter$.pipe(debounceTime(100)).subscribeWhileAlive({
    owner: this,
    next: group => {
      this.getSubmissionHistory();
    }
  });

  private getSubmissionHistory(): void {
    this._isLoading.next(true);
    this.submissionGroupFilter$
      .pipe(
        take(1),
        switchMap(group => {
          const opts = new ODataQueryOptions();
          opts.setOrderBy('lastStatusUpdateDate desc');
          if (!!group && group.length > 0) {
            const ids = group.map(id => `'${id}'`).join(', ');
            opts.setFilter(`submissionTypeId in (${ids})`);
          }
          return this.submissionDomainModel
            .getMemberSubmissions(opts)
            .pipe(finalize(() => this._isLoading.next(false)));
        })
      )
      .subscribe({
        error: () => {
          this._memberSubmissions.next(null);
          this.toastService.publishErrorMessage($localize`Error`, $localize`Unable to get submissions for user.`);
        },
        next: res => {
          this._memberSubmissions.next(res.value);
        }
      });
  }

  public refetchSubmissionHistory(): void {
    this.getSubmissionHistory();
  }
}
